'use client';
import type { ComponentProps, ReactNode } from 'react';
import { CountryCurrency, getCartSummary, getFilteredHalloweenPromoItems, useCartService } from '@skin-seoul/service';
import { LegacyCartIcon } from '../../assets/LegacyCartIcon';
import QueryBoundary from '../../utils/react/QueryBoundary';
import type { Country } from '@skin-seoul/service';
import { ClientOnly } from '@skin-seoul/utils';

const SHOPPING_BAG_BUTTON_TEST_ID = 'header-shopping-bag-button';
// The settled count, the pre-fetch fallback and the failed-fetch fallback all
// render the same glyph shape, so a single testid could not tell "cart is
// genuinely empty" from "still loading" or "cart request failed" — all three
// read as 0. Three distinct testids let a test wait for the settled state
// instead of asserting against a fallback.
const CART_BADGE_COUNT_TEST_ID = 'header-cart-badge-count';
const CART_BADGE_LOADING_TEST_ID = 'header-cart-badge-loading';
const CART_BADGE_ERROR_TEST_ID = 'header-cart-badge-error';

const ProductQuantity = ({ country }: { country: Country }) => {
  const { useGetCartSuspenseQuery } = useCartService();
  const { data } = useGetCartSuspenseQuery({ currency: CountryCurrency[country] });
  const { items } = getCartSummary(data.cart);
  const filteredItems = getFilteredHalloweenPromoItems(items);
  const totalQuantity = filteredItems.reduce((prev, curr) => prev + curr.quantity, 0);

  return (
    <span className="text-label-small text-generic-0" data-testid={CART_BADGE_COUNT_TEST_ID}>
      {totalQuantity}
    </span>
  );
};

type ShoppingBagButtonProps = {
  country: Country;
  iconProps?: ComponentProps<typeof LegacyCartIcon>;
  /**
   * Glyph rendered under the quantity badge. Defaults to the legacy cart icon;
   * the bottom tab bar passes the Figma lucide shopping-bag instead (SSD-5099).
   * `iconProps` only styles the default glyph.
   */
  icon?: ReactNode;
  className?: string;
  onClick?: () => void;
};

const ShoppingBagButton = ({
  country,
  iconProps = { className: 'text-generic-0' },
  icon,
  className,
  onClick,
}: ShoppingBagButtonProps) => {
  return (
    <div
      className={`cursor-pointer w-fit h-fit relative ${className || ''}`}
      data-testid={SHOPPING_BAG_BUTTON_TEST_ID}
      onClick={onClick}
    >
      {icon ?? <LegacyCartIcon cursor="pointer" height="24px" width="24px" {...iconProps} />}
      <ClientOnly>
        <div className="flex items-center bg-[#027A48] rounded-[14px] text-label-small h-4 justify-center absolute -right-1 -top-1 w-4">
          <QueryBoundary
            loadingFallback={
              <span className="text-label-small text-generic-0" data-testid={CART_BADGE_LOADING_TEST_ID}>
                0
              </span>
            }
            errorFallback={
              <span className="text-label-small text-generic-0" data-testid={CART_BADGE_ERROR_TEST_ID}>
                0
              </span>
            }
          >
            <ProductQuantity country={country} />
          </QueryBoundary>
        </div>
      </ClientOnly>
    </div>
  );
};

export default ShoppingBagButton;
